import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AuthGuard } from "@/auth/auth-guard";
import { AppLayout } from "@/layout/app-layout";
import { LoadingSkeleton } from "@/components/shared/loading-skeleton";

// Lazy-loaded page chunks — each becomes a separate JS bundle
const LoginPage = lazy(() => import("@/pages/login"));
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const AuditLogPage = lazy(() => import("@/pages/audit-log"));
const ReportsPage = lazy(() => import("@/pages/reports"));
const DataSubjectSearchPage = lazy(() => import("@/pages/data-subject-search"));
const LineageExplorerPage = lazy(() => import("@/pages/lineage-explorer"));
const SettingsPage = lazy(() => import("@/pages/settings"));
const NotFoundPage = lazy(() => import("@/pages/not-found"));
const AlertsPage = lazy(() => import("@/pages/alerts"));
const ReviewQueuePage = lazy(() => import("@/pages/review-queue"));
const ErasureRequestsPage = lazy(() => import("@/pages/erasure-requests"));
const ConsentManagementPage = lazy(() => import("@/pages/consent-management"));
const RetentionPoliciesPage = lazy(() => import("@/pages/retention-policies"));

const pageFallback = <LoadingSkeleton variant="page" />;

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <Suspense fallback={pageFallback}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    // AuthGuard wraps all protected routes
    element: <AuthGuard />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Redirect root to dashboard
          { index: true, element: <Navigate to="/dashboard" replace /> },
          {
            path: "/dashboard",
            element: (
              <Suspense fallback={pageFallback}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: "/audit",
            element: (
              <Suspense fallback={pageFallback}>
                <AuditLogPage />
              </Suspense>
            ),
          },
          {
            path: "/reports",
            element: (
              <Suspense fallback={pageFallback}>
                <ReportsPage />
              </Suspense>
            ),
          },
          {
            path: "/data-subjects",
            element: (
              <Suspense fallback={pageFallback}>
                <DataSubjectSearchPage />
              </Suspense>
            ),
          },
          {
            path: "/lineage",
            element: (
              <Suspense fallback={pageFallback}>
                <LineageExplorerPage />
              </Suspense>
            ),
          },
          {
            path: "/settings",
            element: (
              <Suspense fallback={pageFallback}>
                <SettingsPage />
              </Suspense>
            ),
          },
          {
            path: "/alerts",
            element: (
              <Suspense fallback={pageFallback}>
                <AlertsPage />
              </Suspense>
            ),
          },
          {
            path: "/review-queue",
            element: (
              <Suspense fallback={pageFallback}>
                <ReviewQueuePage />
              </Suspense>
            ),
          },
          {
            path: "/erasure",
            element: (
              <Suspense fallback={pageFallback}>
                <ErasureRequestsPage />
              </Suspense>
            ),
          },
          {
            path: "/consent",
            element: (
              <Suspense fallback={pageFallback}>
                <ConsentManagementPage />
              </Suspense>
            ),
          },
          {
            path: "/retention",
            element: (
              <Suspense fallback={pageFallback}>
                <RetentionPoliciesPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: (
      <Suspense fallback={pageFallback}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);
